import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import App1 from './App1';
import App2 from './App2';
import InventoryPage from './InventoryPage';
import InventoryHistoryPage from './InventoryHistoryPage';
import PurchaseOrdersPage from './PurchaseOrdersPage';
import ShipmentsPage from './ShipmentsPage';
import AdvisoryBoardDesktop from './AdvisoryBoardDesktop';
import AdvisoryBoardMobile from './AdvisoryBoardMobile';
import reportWebVitals from './reportWebVitals';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

const root = ReactDOM.createRoot(document.getElementById('root'));

const T = '#0D5C63';
const W = '#FFFFFF';
const B = '#D8DADF';
const D = '#1A1A2E';
const G = '#6B7280';

const AUTH_KEY = 'hel_auth';

/** Static Advisor Login credentials (email → password). Login with email + assigned password. */
const ADVISOR_STATIC_LOGINS = [
  { email: 'taylor@theindustrycollective.org', password: 'K7@m2#Px9', first_name: 'Taylor', last_name: 'Foxman' },
  { email: 'johntrainer@paradisespirits.com', password: 'R4!z8@Qn6', first_name: 'John', last_name: 'Trainer' },
  { email: 'derekmdodge@gmail.com', password: 'T9#v3$Lm7', first_name: 'Derek', last_name: 'Dodge' },
  { email: 'hi@kristinamarino.com', password: 'P6@x2!Jk8', first_name: 'Kristina', last_name: 'Marino' },
  { email: 'bucky.layton@wentevineyards.com', password: 'M8#q5@Rt3', first_name: 'Buxton', last_name: 'Layton' },
  { email: 'magslap.ml@gmail.com', password: 'H3!n7$Wp9', first_name: 'Maggie', last_name: 'Lapcewich' },
  { email: 'mike@99-proof.com', password: 'Z5@k9#Dv2', first_name: 'Mike', last_name: 'Solow' },
];

function getStoredAuth() {
  try {
    return JSON.parse(localStorage.getItem(AUTH_KEY)) || null;
  } catch {
    return null;
  }
}

function LoginPage({ onLogin }) {
  const pub = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const asset = (name) => `${pub}/${encodeURIComponent(name)}`;

  const [tab, setTab] = useState('admin'); // admin | advisor | it
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);

  const decodeJwtPayload = (jwt) => {
    if (!jwt || typeof jwt !== 'string') return null;
    const parts = jwt.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
    try {
      const json = atob(b64 + pad);
      return JSON.parse(json);
    } catch {
      return null;
    }
  };

  const fetchUserDetails = async (email) => {
    const res = await fetch(`https://thehelm.helmsmanimports.com/api/user?email=${encodeURIComponent(email)}`, {
      headers: { Accept: 'application/json' },
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data?.message || data?.detail || 'Failed to load user details.');
    }
    return data;
  };

  const normalizeRoleKey = (s) =>
    String(s ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');

  const isSuperAdminAccount = (accounts) =>
    Array.isArray(accounts) && accounts.some((a) => Number(a?.role_id) === 1 && a?.is_superadmin === true);

  /** Advisor (and similar) accounts from API user details. */
  const isAdvisorRoleAccount = (accounts) =>
    Array.isArray(accounts) && accounts.some((a) => Number(a?.role_id) === 2);

  const isHelmsStaffAccount = (accounts) =>
    Array.isArray(accounts) &&
    accounts.some((a) => {
      if (a?.is_helms_staff === true || a?.isHelmsStaff === true) return true;
      const slug = normalizeRoleKey(
        a?.role_slug ?? a?.slug ?? a?.role_code ?? (typeof a?.role === 'string' ? a.role : '')
      );
      if (slug === 'helms_staff' || slug === 'helmsman_staff' || slug === 'helms_staff_member') return true;
      const name = normalizeRoleKey(a?.role_name ?? a?.role_title ?? a?.name);
      if (name.includes('helms') && name.includes('staff')) return true;
      return false;
    });

  const truthyFlag = (v) => v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';

  /** API marks internal Helms users with is_admin (is_staff may be null). */
  const isPortalUserFromProfile = (profile) => {
    if (!profile || typeof profile !== 'object') return false;
    if (truthyFlag(profile.is_admin)) return true;
    if (truthyFlag(profile.is_staff)) return true;
    return false;
  };

  const userProfileFromDetails = (details) => {
    if (!details || typeof details !== 'object') return null;
    if (details.user && typeof details.user === 'object') return details.user;
    return details;
  };

  const isAllowedPortalAccess = (accounts, loginUser, detailsRaw) => {
    const detailsProfile = userProfileFromDetails(detailsRaw);
    return (
      isSuperAdminAccount(accounts) ||
      isAdvisorRoleAccount(accounts) ||
      isHelmsStaffAccount(accounts) ||
      isPortalUserFromProfile(loginUser) ||
      isPortalUserFromProfile(detailsProfile)
    );
  };

  const finishApiAuth = async (data) => {
    const user = data?.user;
    const email = user?.email;
    if (!email) {
      setError('Login failed.');
      return;
    }

    try {
      const details = await fetchUserDetails(email);
      const accounts = details?.accounts || [];
      if (!isAllowedPortalAccess(accounts, user, details)) {
        setError('Access denied.');
        return;
      }

      const detailsProfile = userProfileFromDetails(details);
      const mergedUserRaw =
        detailsProfile && typeof detailsProfile === 'object' ? { ...detailsProfile, ...user } : user;
      const { password: _pw, password_hash: _pwh, ...mergedUser } = mergedUserRaw || {};

      const authData = { token: data.token, accessToken: data.accessToken, user: mergedUser, accounts };
      localStorage.setItem(AUTH_KEY, JSON.stringify(authData));
      onLogin(authData);
    } catch (e) {
      setError(e?.message || 'Network error. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const u = (username || '').trim().toLowerCase();
      const p = password || '';

      let user = null;
      if (tab === 'admin') {
        if (u === 'admin' && p === 'Km8#xP2z') {
          user = {
            id: 'static-admin',
            first_name: 'Admin',
            last_name: '',
            email: 'admin@local',
            image_url: null,
            timezone: null,
            deleted_at: null,
            is_admin: true,
            alpha_id: null,
            is_staff: true,
          };
        }

        if (!user && u === 'helms_staff' && p === 'Km8#xP2z') {
          user = {
            id: 'static-helms-staff',
            first_name: 'Helms',
            last_name: 'Staff',
            email: 'helms_staff@local',
            image_url: null,
            timezone: null,
            deleted_at: null,
            is_admin: false,
            alpha_id: null,
            is_staff: true,
          };
        }

        if (user) {
          const authData = { token: 'static', accessToken: 'static', user };
          localStorage.setItem(AUTH_KEY, JSON.stringify(authData));
          onLogin(authData);
          return;
        }

        const res = await fetch('https://thehelm.helmsmanimports.com/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ email: username, password }),
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data?.message || data?.detail || 'Invalid credentials.');
          return;
        }
        await finishApiAuth(data);
        return;
      }

      if (tab === 'advisor') {
        if (u === 'advisor' && p === 'Br9@vL4q') {
          user = {
            id: 'static-advisor',
            first_name: 'Advisor',
            last_name: '',
            email: 'advisor@local',
            image_url: null,
            timezone: null,
            deleted_at: null,
            is_admin: true,
            alpha_id: null,
            is_staff: true,
          };
        }

        if (!user) {
          const match = ADVISOR_STATIC_LOGINS.find(
            (a) => a.email === u && a.password === p
          );
          if (match) {
            user = {
              id: `static-advisor-${match.email}`,
              first_name: match.first_name,
              last_name: match.last_name,
              email: match.email,
              image_url: null,
              timezone: null,
              deleted_at: null,
              is_admin: true,
              alpha_id: null,
              is_staff: true,
            };
          }
        }

        if (user) {
          const authData = { token: 'static', accessToken: 'static', user };
          localStorage.setItem(AUTH_KEY, JSON.stringify(authData));
          onLogin(authData);
          return;
        }

        const res = await fetch('https://thehelm.helmsmanimports.com/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ email: username, password }),
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data?.message || data?.detail || 'Invalid credentials.');
          return;
        }
        await finishApiAuth(data);
        return;
      }

      // IT tab: accept either static password
      if (tab === 'it') {
        if (p === 'Km8#xP2z' || p === 'Br9@vL4q') {
          user = {
            id: 'static-it',
            first_name: 'IT',
            last_name: '',
            email: 'it@local',
            image_url: null,
            timezone: null,
            deleted_at: null,
            is_admin: true,
            alpha_id: null,
            is_staff: true,
          };
        }
        if (!user) {
          setError('Invalid username or password.');
          return;
        }
        const authData = { token: 'static', accessToken: 'static', user };
        localStorage.setItem(AUTH_KEY, JSON.stringify(authData));
        onLogin(authData);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    if (tab !== 'admin' && tab !== 'advisor') return;
    setError('');
    setGoogleLoading(true);
    try {
      const payload = decodeJwtPayload(credentialResponse?.credential);
      const gEmail = payload?.email;
      const gName = payload?.name || [payload?.given_name, payload?.family_name].filter(Boolean).join(' ').trim();
      if (!gEmail || !gName) {
        setError('Google sign-in failed. Missing profile details.');
        return;
      }

      const res = await fetch('https://thehelm.helmsmanimports.com/api/auth/google', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email: gEmail, name: gName }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.message || data?.detail || 'Google login failed.');
        return;
      }
      await finishApiAuth(data);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '13px 14px',
    fontSize: 14,
    fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
    border: `1px solid ${B}`,
    borderRadius: 8,
    outline: 'none',
    color: D,
    background: W,
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: '#F4F5F7',
        boxSizing: 'border-box',
      }}
    >
      <img
        src={asset('hel.png')}
        alt="Helmsman Imports"
        style={{ maxWidth: 'min(100%, 320px)', width: '100%', height: 'auto', marginBottom: 32, display: 'block' }}
      />
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: W,
          borderRadius: 14,
          boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
          padding: '36px 32px',
          boxSizing: 'border-box',
        }}
      >
        <h2
          style={{
            margin: '0 0 6px',
            fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
            fontSize: 22,
            fontWeight: 700,
            color: D,
          }}
        >
          Sign in
        </h2>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: G, fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif' }}>
          {tab === 'admin' ? 'Helmsman Admin' : tab === 'advisor' ? 'Advisor Login' : 'IT Login'}
        </p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          {[
            { id: 'admin', label: 'Helmsman Admin' },
            { id: 'advisor', label: 'Advisor Login' },
            { id: 'it', label: 'IT Login' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setTab(t.id);
                setError('');
              }}
              style={{
                flex: 1,
                padding: '10px 10px',
                borderRadius: 10,
                border: `1px solid ${tab === t.id ? T : B}`,
                background: tab === t.id ? `${T}10` : W,
                color: D,
                fontSize: 12,
                fontWeight: 700,
                fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && (
          <div
            style={{
              marginBottom: 18,
              padding: '11px 14px',
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: 8,
              color: '#B91C1C',
              fontSize: 13,
              fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="on">
          <label style={{ display: 'block', marginBottom: 16 }}>
            <span style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: D, fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif' }}>
              Username
            </span>
            <input
              type="text"
              autoComplete="username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={tab === 'admin' ? 'Email, Admin, or Helms_staff' : tab === 'advisor' ? 'Email or advisor' : 'Any username'}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = T)}
              onBlur={(e) => (e.target.style.borderColor = B)}
            />
          </label>

          <label style={{ display: 'block', marginBottom: 24 }}>
            <span style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: D, fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif' }}>
              Password
            </span>
            <div style={{ position: 'relative' }}>
              <input
                type={showPw ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{ ...inputStyle, paddingRight: 44 }}
                onFocus={(e) => (e.target.style.borderColor = T)}
                onBlur={(e) => (e.target.style.borderColor = B)}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: G,
                  fontSize: 12,
                  fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
                  padding: 0,
                }}
              >
                {showPw ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: loading ? '#5a9ea3' : T,
              color: W,
              border: 'none',
              borderRadius: 8,
              fontSize: 15,
              fontWeight: 600,
              fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {(tab === 'admin' || tab === 'advisor') && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0' }}>
              <div style={{ height: 1, background: B, flex: 1 }} />
              <span style={{ fontSize: 12, color: G, fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif' }}>or</span>
              <div style={{ height: 1, background: B, flex: 1 }} />
            </div>

            <div style={{ opacity: googleLoading ? 0.7 : 1, pointerEvents: googleLoading ? 'none' : 'auto' }}>
              <GoogleLogin onSuccess={handleGoogleLogin} onError={() => setError('Google sign-in failed. Please try again.')} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LandingPage({ auth, onLogout }) {
  const pub = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const asset = (name) => `${pub}/${encodeURIComponent(name)}`;
  const [showResources, setShowResources] = useState(false);
  const [showGroundtruth, setShowGroundtruth] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const helmsmanResources = [
    {
      title: 'About Helmsman Imports',
      href: 'https://helmsmanimports.com/about/',
      description: 'Learn more about Helmsman Imports, our mission, and the team behind the platform.',
      icon: '🌐',
    },
    {
      title: 'Helmsman Test Page',
      href: 'https://helmsmanimports.com/test/',
      description: 'Testing and preview environment for reviewing upcoming implementations and features.',
      icon: '🌐',
    },
    {
      title: 'FAQs',
      href: 'https://helmsmanimports.com/faqs-3/',
      description: 'Find answers to common questions regarding Helmsman services, workflows, and platform usage.',
      icon: '🌐',
    },
    {
      title: 'Gen Z Alcohol Trends in the U.S. Spirits Market',
      href: 'https://helmsmanimports.com/gen-z-alcohol-trends-us-spirits-market/',
      description:
        'How Gen Z is reshaping drinking habits, brand loyalty, and category growth—and what importers and distributors should watch in the U.S. spirits market.',
      icon: '🌐',
    },
    {
      title: 'What We Learned at BCB Brooklyn: Why Access Matters',
      description:
        'BCB Brooklyn brought the industry together. Reflections on building spirits brands in the U.S. market — and why access is everything.',
      icon: '🥃',
      filename: 'bcb_brooklyn_helmsman.html',
      tabName: 'BCB Brooklyn - Why Access Matters',
    },
    {
      title: "New York Rum Fest 2026: Why Education Remains Rum's Biggest Opportunity",
      description:
        'Why consumer education is the most important driver of premium rum\'s future growth in the U.S. market.',
      icon: '🍹',
      filename: 'ny_rum_fest_helmsman.html',
      tabName: 'NY Rum Fest 2026',
    },
    {
      title: 'How to Switch Alcohol Importers Without Losing Ground',
      description:
        'A practical guide to switching importers — what the transition involves, what doesn\'t change, and how to do it without losing ground.',
      icon: '🔄',
      filename: 'switch_importers_helmsman.html',
      tabName: 'Switch Alcohol Importers',
    },
    {
      title: 'What the Three-Tier System Actually Costs Your Brand',
      description:
        'Jeff Diego breaks down the visible and invisible costs of the three-tier system for founder-led spirits brands entering the U.S. market.',
      icon: '📊',
      filename: 'three_tier_system_helmsman.html',
      tabName: 'Three-Tier System Costs',
    },
  ];

  const groundtruthReports = [
    {
      title: 'Groundtruth - BCB Brooklyn Report',
      description:
        'GroundTruth campaign performance dashboard — overview, campaigns, geography, and recommendations for BCB Brooklyn.',
      icon: '📊',
      filename: 'groundtruth-bcb-brooklyn-report.html',
      tabName: 'Groundtruth - BCB Brooklyn Report',
    },
    {
      title: 'TOTC · Calculator · Spirited Awards',
      description:
        'Campaign wrap dashboard for Calculator, TOTC, and Spirited Awards — final impressions, clicks, CTR, and reach.',
      icon: '📈',
      filename: 'Helmsman_Calculator_TOTC_and_SpiritedAwards_Campaign_Dashboard.html',
      tabName: 'TOTC · Calculator · Spirited Awards',
    },
    {
      title: 'Website Baseline vs. Campaign Performance',
      description:
        'Baseline vs. campaign dashboard — how each campaign performed against website baseline and what happens after.',
      icon: '📉',
      filename: 'Helmsman_Baseline_vs_Campaign_Dashboard.html',
      tabName: 'Baseline vs. Campaign',
    },
    {
      title: 'Our Website, In Plain English',
      description:
        'Stakeholder story of the Helmsman website — what it does, who it serves, and why it matters, in plain English.',
      icon: '📝',
      filename: 'Helmsman_Website_Story_For_Stakeholders.html',
      tabName: 'Website Story for Stakeholders',
    },
  ];

  const ui = {
    font: 'system-ui, -apple-system, Segoe UI, sans-serif',
    bg: 'radial-gradient(1100px 520px at 50% -120px, rgba(13,92,99,0.22), rgba(244,245,247,0) 72%), linear-gradient(#F9FAFB, #F4F5F7)',
    cardShadow: '0 1px 2px rgba(0,0,0,0.05), 0 10px 24px rgba(0,0,0,0.03)',
    cardShadowHover: `0 16px 40px ${T}26`,
    border: `1px solid ${B}`,
    borderHover: `1px solid ${T}`,
  };

  const go = (pathname) => {
    window.history.pushState({}, '', pathname);
  };

  const encodeFilename = (filename) => {
    const [path, hash] = String(filename).split('#');
    const encoded = path.split('/').map(encodeURIComponent).join('/');
    return hash ? `${encoded}#${hash}` : encoded;
  };

  const openDemo = (filename) => {
    const base = window.location.origin + pub;
    window.location.href = `${base}/${encodeFilename(filename)}`;
  };

  const downloadFile = (filename, downloadAs) => {
    const base = window.location.origin + pub;
    const href = `${base}/${encodeFilename(filename)}`;
    const a = document.createElement('a');
    a.href = href;
    a.download = downloadAs || filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const openExternal = (href) => {
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const openDemoNewTab = (filename, tabName) => {
    const base = window.location.origin + pub;
    const href = `${base}/${encodeFilename(filename)}`;
    window.open(href, tabName || '_blank', 'noopener,noreferrer');
  };

  const landingItems = [
    {
      label: 'Logistic & Compliance',
      sub: 'Logistics & compliance demo',
      icon: '🚢',
      kind: 'btn',
      onClick: () => openDemo('helmsman_logistics_compliance.html'),
    },
    {
      label: 'Logistic & Compliance v2',
      sub: 'Updated logistics & compliance demo',
      icon: '🚢',
      kind: 'btn',
      onClick: () => openDemo('helmsman_logistics_compliance_2.html'),
    },
    {
      label: 'Accounts Receivable & COLA TTB',
      sub: 'New finance and compliance pages',
      icon: '📑',
      kind: 'btn',
      onClick: () => openDemo('helmsman_ar_cola_ttb.html'),
    },
    {
      label: 'Admin',
      sub: 'Admin portal demo',
      icon: '🛠',
      kind: 'btn',
      onClick: () => openDemo('helmsman_admin.html'),
    },
    {
      label: 'Helmsman Website Resources',
      sub: 'Guides, FAQs, and useful links',
      icon: '📚',
      kind: 'btn',
      onClick: () => setShowResources(true),
    },
    {
      label: 'Advisory Board Intro to Helmsman (Desktop)',
      sub: 'Desktop intro',
      icon: '🖥',
      kind: 'advisory',
      openFilename: 'Advisory Board Intro to Helmsman.html',
      downloadAs: 'Advisory Board Intro to Helmsman (Desktop).html',
    },
    {
      label: 'Three-Tier Learning Board',
      sub: 'Interactive learning board',
      icon: '🧭',
      kind: 'btn',
      onClick: () => openDemo('three-tier-learning-board.html'),
    },
    {
      label: 'Report',
      sub: 'GroundTruth campaign performance dashboards',
      icon: '📊',
      kind: 'btn',
      onClick: () => setShowGroundtruth(true),
    },
    {
      label: 'App V1',
      sub: 'React app — /pre',
      icon: '⚛',
      kind: 'btn',
      onClick: () => go('/pre'),
    },
    {
      label: 'App v2',
      sub: 'React app — /latest',
      icon: '⚡',
      kind: 'btn',
      onClick: () => go('/latest'),
    },
    {
      label: 'Advisory Board Intro to Helmsman (Mobile)',
      sub: 'Mobile intro',
      icon: '📱',
      kind: 'advisory',
      openFilename: 'Advisory Board Intro to Helmsman - Mobile.html',
      downloadAs: 'Advisory Board Intro to Helmsman (Mobile).html',
    },
    {
      label: 'Direct Order Form',
      sub: 'Direct order form demo',
      icon: '📋',
      kind: 'btn',
      onClick: () => openDemoNewTab('helmsman-direct-order-form.html', 'Direct Order Form'),
    },
    // Hidden: original v1 app (public/helmsman-mobile-app-v1.html)
    // {
    //   label: 'THE HELM Mobile',
    //   sub: 'Standalone mobile app — brand, SKU, and market data',
    //   icon: '📱',
    //   kind: 'btn',
    //   onClick: () => openDemoNewTab('helmsman-mobile-app.html', 'THE HELM'),
    // },
    {
      label: 'THE HELM Mobile',
      sub: 'Standalone mobile app',
      icon: '📱',
      kind: 'btn',
      onClick: () => openDemoNewTab('helmsman-mobile-app.html', 'THE HELM'),
    },
    {
      label: 'HelmScan',
      sub: 'TTB COLA, importer & wholesaler intelligence',
      icon: '🔍',
      kind: 'btn',
      onClick: () => openDemoNewTab('helmscan.html', 'HelmScan'),
    },
    {
      label: 'Team Time Tracker',
      sub: 'Team time tracking prototype',
      icon: '⏱',
      kind: 'btn',
      onClick: () => openDemoNewTab('helmsman-time-tracker.html', 'Team Time Tracker'),
    },
  ];

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredLandingItems = landingItems.filter((item) => {
    if (!normalizedSearch) return true;
    return `${item.label} ${item.sub}`.toLowerCase().includes(normalizedSearch);
  });

  const modalOpen = showResources || showGroundtruth;

  useEffect(() => {
    if (!modalOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowResources(false);
        setShowGroundtruth(false);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [modalOpen]);

  const renderResourceModal = ({
    show,
    onClose,
    titleId,
    badge,
    title,
    description,
    items,
    onOpenItem,
    footer,
    zIndex = 200,
  }) => {
    if (!show) return null;

    const itemCount = items.length;
    const isSingle = itemCount === 1;
    const modalMaxWidth =
      itemCount <= 1 ? 520 : itemCount === 2 ? 720 : itemCount <= 4 ? 960 : 1120;
    const gridTemplateColumns =
      itemCount <= 1
        ? '1fr'
        : itemCount === 2
          ? 'repeat(2, minmax(0, 1fr))'
          : 'repeat(auto-fit, minmax(280px, 1fr))';

    const openBtn = (item) => (
      <button
        type="button"
        onClick={() => onOpenItem(item)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          justifySelf: 'center',
          gap: 8,
          minHeight: 38,
          padding: isSingle ? '10px 16px' : '8px 12px',
          borderRadius: 10,
          border: 'none',
          background: T,
          color: W,
          fontFamily: ui.font,
          fontSize: 12.5,
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: '0 10px 20px rgba(13, 92, 99, 0.2)',
          ...(isSingle ? { width: 'fit-content', marginTop: 12 } : { marginTop: 'auto', width: '100%' }),
        }}
      >
        {item.openLabel || 'Open in new tab'} <span aria-hidden="true">↗</span>
      </button>
    );

    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.55)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          zIndex,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: `min(${modalMaxWidth}px, 100%)`,
            maxHeight: 'min(90vh, 860px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
            border: `1px solid ${B}`,
            borderRadius: 22,
            boxShadow: '0 32px 80px rgba(15, 23, 42, 0.22)',
            padding: isSingle ? '20px 22px' : 22,
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 16,
              marginBottom: isSingle ? 14 : 18,
              flexShrink: 0,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <h2
                id={titleId}
                style={{
                  margin: '0 0 6px',
                  color: D,
                  fontFamily: ui.font,
                  fontSize: isSingle ? 22 : 24,
                  lineHeight: 1.15,
                }}
              >
                {title}
              </h2>
              <p
                style={{
                  margin: 0,
                  color: G,
                  fontFamily: ui.font,
                  fontSize: 13,
                  lineHeight: 1.45,
                  maxWidth: isSingle ? '100%' : 760,
                }}
              >
                {description}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                width: 42,
                height: 42,
                flexShrink: 0,
                borderRadius: 12,
                border: `1px solid ${B}`,
                background: W,
                color: D,
                cursor: 'pointer',
                fontSize: 22,
                lineHeight: 1,
                boxShadow: '0 6px 14px rgba(15, 23, 42, 0.08)',
              }}
            >
              ×
            </button>
          </div>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingRight: 4,
              marginRight: -4,
            }}
          >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns,
              gap: 12,
              width: '100%',
            }}
          >
            {items.map((item) =>
              isSingle ? (
                <div
                  key={item.title}
                  style={{
                    display: 'flex',
                    gap: 14,
                    padding: '16px 18px',
                    borderRadius: 16,
                    border: `1px solid ${B}`,
                    background: 'rgba(255,255,255,0.95)',
                    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.06)',
                  }}
                >
                  {item.icon ? (
                    <div
                      aria-hidden="true"
                      style={{
                        width: 48,
                        height: 48,
                        flexShrink: 0,
                        borderRadius: 14,
                        display: 'grid',
                        placeItems: 'center',
                        background: `${T}10`,
                        border: `1px solid ${T}22`,
                        fontSize: 22,
                        lineHeight: 1,
                      }}
                    >
                      {item.icon}
                    </div>
                  ) : null}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        marginBottom: 4,
                        color: D,
                        fontFamily: ui.font,
                        fontSize: 15,
                        fontWeight: 800,
                        lineHeight: 1.25,
                      }}
                    >
                      {item.title}
                    </div>
                    <div
                      style={{
                        color: G,
                        fontFamily: ui.font,
                        fontSize: 12.5,
                        lineHeight: 1.45,
                      }}
                    >
                      {item.description}
                    </div>
                    {openBtn(item)}
                  </div>
                </div>
              ) : (
                <div
                  key={item.title}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    padding: 16,
                    borderRadius: 16,
                    border: `1px solid ${B}`,
                    background: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.06)',
                  }}
                >
                  <div>
                    {item.icon ? (
                      <div
                        aria-hidden="true"
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 12,
                          display: 'grid',
                          placeItems: 'center',
                          background: `${T}10`,
                          border: `1px solid ${T}22`,
                          fontSize: 18,
                          lineHeight: 1,
                          marginBottom: 10,
                        }}
                      >
                        {item.icon}
                      </div>
                    ) : null}
                    <div
                      style={{
                        marginBottom: 6,
                        color: D,
                        fontFamily: ui.font,
                        fontSize: 15,
                        fontWeight: 800,
                        lineHeight: 1.25,
                      }}
                    >
                      {item.title}
                    </div>
                    <div
                      style={{
                        color: G,
                        fontFamily: ui.font,
                        fontSize: 12.5,
                        lineHeight: 1.45,
                      }}
                    >
                      {item.description}
                    </div>
                  </div>
                  {openBtn(item)}
                </div>
              )
            )}
          </div>
          </div>
          {footer ? (
            <div
              style={{
                padding: '12px 14px',
                borderRadius: 14,
                border: `1px solid ${T}22`,
                background: `${T}10`,
                color: D,
                fontFamily: ui.font,
                flexShrink: 0,
                marginTop: 14,
              }}
            >
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const btn = (label, sub, onClick, icon) => (
    <button
      type="button"
      key={label}
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        width: '100%',
        padding: '20px 18px 18px',
        textAlign: 'center',
        fontFamily: ui.font,
        fontSize: 14,
        fontWeight: 700,
        color: D,
        background: W,
        border: ui.border,
        borderRadius: 12,
        cursor: 'pointer',
        boxShadow: ui.cardShadow,
        transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
        alignItems: 'center',
        minHeight: 108,
        position: 'relative',
        outline: 'none',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.border = ui.borderHover;
        e.currentTarget.style.boxShadow = ui.cardShadowHover;
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.border = ui.border;
        e.currentTarget.style.boxShadow = ui.cardShadow;
        e.currentTarget.style.transform = 'translateY(0px)';
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 44,
          height: 44,
          borderRadius: 14,
          display: 'grid',
          placeItems: 'center',
          background: `${T}10`,
          border: `1px solid ${T}22`,
          marginBottom: 10,
        }}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>{icon || '↗'}</span>
      </div>
      <span style={{ display: 'block', color: D, letterSpacing: 0.1 }}>{label}</span>
      {sub ? (
        <span style={{ display: 'block', marginTop: 4, fontSize: 11.5, fontWeight: 500, color: G }}>{sub}</span>
      ) : null}
    </button>
  );

  const advisoryWithDownload = (label, sub, openFilename, downloadAs, icon) => (
    <button
      type="button"
      key={label}
      onClick={() => openDemo(openFilename)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        width: '100%',
        padding: '20px 18px 18px',
        textAlign: 'center',
        fontFamily: ui.font,
        fontSize: 14,
        fontWeight: 700,
        color: D,
        background: W,
        border: ui.border,
        borderRadius: 12,
        cursor: 'pointer',
        boxShadow: ui.cardShadow,
        transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
        alignItems: 'center',
        minHeight: 108,
        position: 'relative',
        outline: 'none',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.border = ui.borderHover;
        e.currentTarget.style.boxShadow = ui.cardShadowHover;
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.border = ui.border;
        e.currentTarget.style.boxShadow = ui.cardShadow;
        e.currentTarget.style.transform = 'translateY(0px)';
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 44,
          height: 44,
          borderRadius: 14,
          display: 'grid',
          placeItems: 'center',
          background: `${T}10`,
          border: `1px solid ${T}22`,
          marginBottom: 10,
        }}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>{icon || '📄'}</span>
      </div>
      <span style={{ display: 'block', color: D, letterSpacing: 0.1 }}>{label}</span>
      {sub ? (
        <span style={{ display: 'block', marginTop: 4, fontSize: 11.5, fontWeight: 500, color: G }}>{sub}</span>
      ) : null}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          downloadFile(openFilename, downloadAs);
        }}
        title="Save as PDF"
        aria-label="Save as PDF"
        style={{
          position: 'absolute',
          right: 10,
          bottom: 10,
          width: 34,
          height: 34,
          borderRadius: 10,
          background: '#F3F4F6',
          color: '#111827',
          border: '1px solid #E5E7EB',
          fontSize: 14,
          fontWeight: 800,
          fontFamily: ui.font,
          cursor: 'pointer',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        ⬇
      </button>
    </button>
  );

  const userInitials = auth
    ? `${auth.user.first_name?.[0] || ''}${auth.user.last_name?.[0] || ''}`.toUpperCase() ||
      auth.user.email?.[0]?.toUpperCase() ||
      '?'
    : '';
  const userDisplayName =
    auth && `${auth.user.first_name || ''} ${auth.user.last_name || ''}`.trim()
      ? `${auth.user.first_name || ''} ${auth.user.last_name || ''}`.trim()
      : auth?.user.email || '';
  const userFirstName =
    auth?.user.first_name?.trim() ||
    userDisplayName.split(' ')[0] ||
    auth?.user.email?.split('@')[0] ||
    'there';

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '24px 24px 20px',
        paddingTop: auth ? 88 : 24,
        background: ui.bg,
        boxSizing: 'border-box',
      }}
    >
      {auth && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '12px 24px',
            background: '#D8E0E0',
            backdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${B}`,
            boxSizing: 'border-box',
            zIndex: 100,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <img
              src={asset('hel.png')}
              alt="Helmsman Imports"
              style={{ width: 220, height: 60, objectFit: 'contain', display: 'block', flexShrink: 0 }}
            />
            
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            <div
              aria-hidden="true"
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: `${T}18`,
                color: T,
                display: 'grid',
                placeItems: 'center',
                fontFamily: ui.font,
                fontSize: 13,
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              {userInitials}
            </div>
            <div style={{ minWidth: 0, textAlign: 'left' }}>
              <div
                style={{
                  fontFamily: ui.font,
                  fontSize: 14,
                  fontWeight: 700,
                  color: D,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {userDisplayName}
              </div>
              <div
                style={{
                  fontFamily: ui.font,
                  fontSize: 12,
                  color: G,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {auth.user.email}
              </div>
            </div>
            <button
              type="button"
              onClick={onLogout}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 14px',
                background: W,
                border: `1px solid ${B}`,
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                color: D,
                cursor: 'pointer',
                fontFamily: ui.font,
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      )}
      
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
        }}
      >
      {/* <div
        style={{
          fontFamily: ui.font,
          fontSize: 18,
          fontWeight: 900,
          color: D,
          letterSpacing: 0.1,
          marginBottom: 6,
          textAlign: 'center',
        }}
      >
        Admin Portal
      </div> */}
      <div style={{ textAlign: 'center', marginBottom: 22, maxWidth: 760, width: '100%', padding: '0 8px' }}>
        <h1
          style={{
            margin: '15px 5px 10px',
            fontFamily: ui.font,
            fontSize: 'clamp(26px, 4vw, 34px)',
            fontWeight: 800,
            color: T,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
          }}
        >
          Welcome back, {userFirstName}! 👋
        </h1>
        <p
          style={{
            margin: 0,
            fontFamily: ui.font,
            fontSize: 15,
            color: G,
            lineHeight: 1.55,
          }}
        >
          Your central hub for Helmsman tools, demos, and resources.
        </p>
      </div>
      <div
        style={{
          width: '50%',
          minWidth: 280,
          maxWidth: 480,
          marginBottom: 20,
          position: 'relative',
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 14,
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: G,
          }}
        >
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search tools, demos, and resources..."
          aria-label="Search tools, demos, and resources"
          style={{
            width: '100%',
            padding: '13px 42px 13px 42px',
            fontSize: 14,
            fontFamily: ui.font,
            border: `1px solid ${B}`,
            borderRadius: 12,
            outline: 'none',
            color: D,
            background: W,
            boxShadow: ui.cardShadow,
            boxSizing: 'border-box',
            transition: 'border-color 0.15s, box-shadow 0.15s',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = T;
            e.target.style.boxShadow = `0 0 0 3px ${T}18`;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = B;
            e.target.style.boxShadow = ui.cardShadow;
          }}
        />
        {searchQuery ? (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            aria-label="Clear search"
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 28,
              height: 28,
              borderRadius: 8,
              border: 'none',
              background: '#F3F4F6',
              color: G,
              cursor: 'pointer',
              fontFamily: ui.font,
              fontSize: 16,
              lineHeight: 1,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            ×
          </button>
        ) : null}
      </div>
      <div
        style={{
          width: '100%',
          maxWidth: 1200,
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
          gap: 16,
        }}
      >
        {filteredLandingItems.length === 0 ? (
          <div
            style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '36px 16px',
              borderRadius: 14,
              border: `1px dashed ${B}`,
              background: 'rgba(255,255,255,0.7)',
              color: G,
              fontFamily: ui.font,
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            No tools match <strong style={{ color: D }}>&ldquo;{searchQuery.trim()}&rdquo;</strong>. Try a different search term.
          </div>
        ) : (
          filteredLandingItems.map((item) =>
            item.kind === 'advisory'
              ? advisoryWithDownload(
                  item.label,
                  item.sub,
                  item.openFilename,
                  item.downloadAs,
                  item.icon
                )
              : btn(item.label, item.sub, item.onClick, item.icon)
          )
        )}
      </div>
      </div>
      <footer
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          paddingTop: 24,
          marginTop: 'auto',
          fontFamily: ui.font,
          fontSize: 13,
          color: G,
          flexShrink: 0,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
            stroke={T}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9 12l2 2 4-4"
            stroke={T}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>
          Secure<span style={{ margin: '0 6px', opacity: 0.45 }}>·</span>
          Reliable<span style={{ margin: '0 6px', opacity: 0.45 }}>·</span>
          Built for Helmsman
        </span>
      </footer>
      {renderResourceModal({
        show: showResources,
        onClose: () => setShowResources(false),
        titleId: 'helmsman-resources-title',
        badge: 'Helmsman Website Resources',
        title: 'Helmsman Website Resources',
        description:
          'Open any resource in a new tab for articles, company information, FAQs, and preview content.',
        items: helmsmanResources,
        onOpenItem: (resource) => {
          if (resource.filename) {
            openDemoNewTab(resource.filename, resource.tabName);
            return;
          }
          openExternal(resource.href);
        },
        footer: (
          <>
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 4 }}>Password-protected links</div>
            <div style={{ fontSize: 12.5, lineHeight: 1.45 }}>
              If prompted for a password, use <strong>helmsman2026</strong>.
            </div>
          </>
        ),
      })}
      {renderResourceModal({
        show: showGroundtruth,
        onClose: () => setShowGroundtruth(false),
        titleId: 'groundtruth-reports-title',
        badge: 'GroundTruth Reports',
        title: 'Report',
        description:
          'Open any GroundTruth performance dashboard in a new tab for campaign overview, geography, and recommendations.',
        items: groundtruthReports,
        onOpenItem: (report) =>
          openDemoNewTab(report.filename, report.tabName),
      })}
    </div>
  );
}

function RouteSwitch() {
  const [path, setPath] = useState(window.location.pathname || '/');
  const [auth, setAuth] = useState(() => getStoredAuth());

  useEffect(() => {
    const sync = () => setPath(window.location.pathname || '/');
    window.addEventListener('popstate', sync);
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    window.history.pushState = (...args) => { originalPushState.apply(window.history, args); sync(); };
    window.history.replaceState = (...args) => { originalReplaceState.apply(window.history, args); sync(); };
    return () => {
      window.removeEventListener('popstate', sync);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  const handleLogin = (authData) => {
    setAuth(authData);
    window.history.pushState({}, '', '/landing');
  };
  const handleLogout = () => {
    localStorage.removeItem(AUTH_KEY);
    setAuth(null);
    window.history.pushState({}, '', '/');
  };

  if (!auth) {
    if (path !== '/') window.history.replaceState({}, '', '/');
    return <LoginPage onLogin={handleLogin} />;
  }

  if (path === '/' || path === '/landing') return <LandingPage auth={auth} onLogout={handleLogout} />;
  if (path === '/inventory' || path.startsWith('/inventory/')) return <InventoryPage />;
  if (path === '/history' || path.startsWith('/history/')) return <InventoryHistoryPage />;
  if (path === '/purchase-orders' || path.startsWith('/purchase-orders/')) return <PurchaseOrdersPage />;
  if (path === '/shipments' || path.startsWith('/shipments/')) return <ShipmentsPage />;
  if (path === '/latest' || path.startsWith('/latest/')) return <App2 />;
  if (path === '/pre' || path.startsWith('/pre/')) return <App />;
  if (path === '/advisory-board' || path.startsWith('/advisory-board/')) return <AdvisoryBoardDesktop />;
  if (path === '/advisory-board-mobile' || path.startsWith('/advisory-board-mobile/')) return <AdvisoryBoardMobile />;
  return <App1 />;
}

root.render(
  <React.StrictMode>
    <GoogleOAuthProvider clientId="651557748891-hvot87sk0girtqklrd2s1i9bb7hi63j0.apps.googleusercontent.com">
      <RouteSwitch />
    </GoogleOAuthProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
